import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography
} from '@mui/material';
import api from '../../api';
import {
  MarginLadderTierResolved,
  TenantMarginTierDto
} from '../../api/marginLadder';
import RegularInput from '../input/RegularInput';
import SettingsCard from './SettingsCard';

/**
 * Calculator v3 — tenant-level Margin Ladder editor.
 *
 * This is the canonical place operators define their margin floors. Each
 * tier covers a monthly-contract-size band and specifies three thresholds:
 *   Floor       hard gate (approval required to submit below this)
 *   Acceptable  soft warning
 *   Win         target
 *
 * Lives in Company Settings, not on the per-lead Analysis screen — the
 * ladder governs every future bid for this tenant. Per-lead overrides
 * (below-floor approval) are handled separately in the proposal flow.
 *
 * Silently hides for tenants without calculator_v3_enabled (the list
 * endpoint 403s).
 */
const MarginLadderSettingsCard: React.FC = function MarginLadderSettingsCard() {
  const [tiers, setTiers] = useState<TenantMarginTierDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snackOpen, setSnackOpen] = useState<boolean>(false);
  const [isGlobalDefault, setIsGlobalDefault] = useState<boolean>(true);
  const [hidden, setHidden] = useState<boolean>(false);

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const list = await api.MarginLadderApi.list();
      setTiers(
        list.map((t: MarginLadderTierResolved) => ({
          acceptableMargin: Number(t.acceptableMargin),
          contractSizeMax:
            t.contractSizeMax === null ? null : Number(t.contractSizeMax),
          contractSizeMin: Number(t.contractSizeMin),
          floorMargin: Number(t.floorMargin),
          winMargin: Number(t.winMargin)
        }))
      );
      setIsGlobalDefault(
        list.length === 0 || list.every((t) => t.isGlobalDefault)
      );
      setHidden(false);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setHidden(true);
      } else {
        setError(
          err?.response?.data?.message ??
            err?.message ??
            'Failed to load margin ladder.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateTier = (idx: number, patch: Partial<TenantMarginTierDto>) => {
    setTiers((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, ...patch } : t))
    );
  };

  const addTier = () => {
    setTiers((prev) => {
      const last = prev[prev.length - 1];
      const nextMin =
        last?.contractSizeMax !== null && last?.contractSizeMax !== undefined
          ? Number(last.contractSizeMax)
          : 0;
      return [
        ...prev,
        {
          acceptableMargin: 0,
          contractSizeMax: null,
          contractSizeMin: nextMin,
          floorMargin: 0,
          winMargin: 0
        }
      ];
    });
  };

  const removeTier = (idx: number) => {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.MarginLadderApi.replaceLadder(tiers);
      setSnackOpen(true);
      setIsGlobalDefault(false);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          'Save failed. Check that tiers are non-overlapping and floor ≤ acceptable ≤ win.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (hidden) return null;

  return (
    <>
      <SettingsCard
        description="Per-tenant margin floors keyed by monthly contract size. Every new bid is classified against these thresholds. Floor = hard gate (approval required below); Acceptable = soft warning; Win = target."
        title="Margin Ladder"
      >
        <Box sx={{ p: 3 }}>
          {loading && <CircularProgress />}

          {!loading && (
            <>
              <Typography color="secondary.400" sx={{ mb: 2 }} variant="body_100">
                {isGlobalDefault
                  ? 'Currently using Bluberry defaults. Edits below will create your tenant-specific ladder.'
                  : 'Your tenant overrides are active. Delete all rows and save to revert to Bluberry defaults.'}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  fontWeight: 'bold',
                  gap: 1,
                  gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 40px',
                  mb: 1
                }}
              >
                <Typography variant="body_100">Min $/mo</Typography>
                <Typography variant="body_100">Max $/mo</Typography>
                <Typography variant="body_100">Floor %</Typography>
                <Typography variant="body_100">Acceptable %</Typography>
                <Typography variant="body_100">Win %</Typography>
                <Box />
              </Box>

              {tiers.map((tier, idx) => (
                <Box
                  // eslint-disable-next-line react/no-array-index-key
                  key={`tier-${idx}`}
                  sx={{
                    alignItems: 'center',
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 40px',
                    mb: 1
                  }}
                >
                  <RegularInput
                    minVal={0}
                    onChange={(e: any) =>
                      updateTier(idx, { contractSizeMin: Number(e) })
                    }
                    type="number"
                    value={String(tier.contractSizeMin)}
                  />
                  <RegularInput
                    minVal={0}
                    onChange={(e: any) =>
                      updateTier(idx, {
                        contractSizeMax:
                          e === '' || e === null ? null : Number(e)
                      })
                    }
                    type="number"
                    value={
                      tier.contractSizeMax === null ||
                      tier.contractSizeMax === undefined
                        ? ''
                        : String(tier.contractSizeMax)
                    }
                  />
                  <RegularInput
                    minVal={0}
                    onChange={(e: any) =>
                      updateTier(idx, { floorMargin: Number(e) / 100 })
                    }
                    type="number"
                    value={(tier.floorMargin * 100).toFixed(1)}
                  />
                  <RegularInput
                    minVal={0}
                    onChange={(e: any) =>
                      updateTier(idx, { acceptableMargin: Number(e) / 100 })
                    }
                    type="number"
                    value={(tier.acceptableMargin * 100).toFixed(1)}
                  />
                  <RegularInput
                    minVal={0}
                    onChange={(e: any) =>
                      updateTier(idx, { winMargin: Number(e) / 100 })
                    }
                    type="number"
                    value={(tier.winMargin * 100).toFixed(1)}
                  />
                  <Button
                    color="secondary"
                    onClick={() => removeTier(idx)}
                    size="small"
                    sx={{ minWidth: 0 }}
                    variant="text"
                  >
                    ✕
                  </Button>
                </Box>
              ))}

              <Box sx={{ mb: 2, mt: 1 }}>
                <Button color="primary" onClick={addTier} variant="outlined">
                  + Add tier
                </Button>
              </Box>

              <Typography color="secondary.400" variant="body_100">
                Percentages are whole numbers (e.g. 45 for 45%). Leave Max
                blank for the top (open-ended) tier. Tiers must not overlap;
                floor ≤ acceptable ≤ win.
              </Typography>

              {error && (
                <Typography color="error.main" sx={{ mt: 1 }} variant="body_100">
                  {error}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                <Button
                  color="primary"
                  disabled={saving || tiers.length === 0}
                  onClick={save}
                  variant="contained"
                >
                  {saving ? 'Saving…' : 'Save ladder'}
                </Button>
                <Button
                  color="secondary"
                  disabled={saving}
                  onClick={load}
                  variant="text"
                >
                  Reset
                </Button>
              </Box>
            </>
          )}
        </Box>
      </SettingsCard>
      <Snackbar
        autoHideDuration={3000}
        message={`Margin ladder saved: ${tiers.length} tier(s).`}
        onClose={() => setSnackOpen(false)}
        open={snackOpen}
      />
    </>
  );
};

export default MarginLadderSettingsCard;
